import { FC, ReactElement, useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import SwiperType from "swiper/types/swiper-class";
import cx from "classnames";
import theme from "@/theme/theme";
import {
  AlpineIcon,
  BackpackIcon,
  CactusIcon,
  CampIcon,
  CheersIcon,
  DeerIcon,
  FarmIcon,
  FerrisIcon,
  GuitarIcon,
  KayakIcon,
  PalmIcon,
  ParachuteIcon,
  ParkIcon,
  ScubaMaskIcon,
  SignpostIcon,
  SunbedIcon,
  TelescopeIcon,
  TreesIcon,
  TropicalIcon,
  VolcanoIcon,
  CategoriesIcon,
} from "@/components/Icon";
import Typography from "@/components/Typography";
import { Skeleton } from "@/components/Loader";
import { useSwiperSlidesPerView, useResponsive, useTrackers } from "@/hooks";
import MobilePopup from "@/components/MobilePopup";
import styles from "./CategoryFilter.module.scss";

type CategoryType = {
  title: string;
  value: string;
  icon: ReactElement;
  enabled: boolean;
};

type Props = {
  onChangeCategory: (newCategories: Array<string>) => void;
  selectedCategories: Array<string>;
  availableCategories: Array<string>;
  isLoading: boolean;
  totalCount: number;
};

const CategoryFilter: FC<Props> = ({
  onChangeCategory,
  selectedCategories,
  availableCategories,
  isLoading,
  totalCount = 0,
}) => {
  const [firstSwiperObj, setFirstSwiperObj] = useState<SwiperType | undefined>(
    undefined
  );
  const [secondSwiperObj, setSecondSwiperObj] = useState<
    SwiperType | undefined
  >(undefined);
  const [mobileSwiperObj, setMobileSwiperObj] = useState<
    SwiperType | undefined
  >(undefined);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const { isMobile } = useResponsive();
  const { track } = useTrackers();
  const containerRef = useRef<HTMLDivElement>(null);
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const { slidesPerView: firstSlidesPerView } = useSwiperSlidesPerView({
    containerRef,
    slideWidth: 130,
  });

  const { slidesPerView: secondSlidesPerView } = useSwiperSlidesPerView({
    containerRef,
    slideWidth: 140,
  });

  const { slidesPerView: mobileSlidesPerView } = useSwiperSlidesPerView({
    containerRef: mobileContainerRef,
    slideWidth: 112,
  });

  const isSelected = (categoryValue: string) => {
    return selectedCategories?.includes(categoryValue);
  };

  const isEnabled = (categoryValue: string) => {
    return availableCategories?.includes(categoryValue);
  };

  useEffect(() => {
    if (!firstSwiperObj || isLoading) return;
    firstSwiperObj?.slideTo(0, 100);
  }, [firstSwiperObj, isLoading]);

  useEffect(() => {
    if (!secondSwiperObj || isLoading) return;
    secondSwiperObj?.slideTo(0, 100);
  }, [secondSwiperObj, isLoading]);

  const ClimateCategories = [
    {
      title: "Tropical & Beachside",
      value: "tropical_and_beachside",
      icon: <TropicalIcon variant="outlined" />,
      enabled: isEnabled("tropical_and_beachside"),
    },
    {
      title: "Cold Climate & Alpine",
      value: "cold_climate_and_alpine",
      icon: (
        <AlpineIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("cold_climate_and_alpine")
              ? theme.palette.common.grey[300]
              : isSelected("cold_climate_and_alpine")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("cold_climate_and_alpine"),
    },
    {
      title: "Outback & Remote",
      value: "outback_and_remote",
      icon: (
        <CampIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("outback_and_remote")
              ? theme.palette.common.grey[300]
              : isSelected("outback_and_remote")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("outback_and_remote"),
    },
    {
      title: "Rainforest & Eco Escape",
      value: "rainforest_and_eco_escape",
      icon: (
        <TreesIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("rainforest_and_eco_escape")
              ? theme.palette.common.grey[300]
              : isSelected("rainforest_and_eco_escape")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("rainforest_and_eco_escape"),
    },
    {
      title: "Countryside & Rural Escape",
      value: "countryside_and_rural_escape",
      icon: <FarmIcon variant="outlined" />,
      enabled: isEnabled("countryside_and_rural_escape"),
    },
    {
      title: "Desert & Arid Adventure",
      value: "desert_and_arid_adventure",
      icon: <CactusIcon variant="outlined" />,
      enabled: isEnabled("desert_and_arid_adventure"),
    },
    {
      title: "City Buzz",
      value: "city_buzz",
      icon: <FerrisIcon variant="outlined" />,
      enabled: isEnabled("city_buzz"),
    },
    {
      title: "Island Life",
      value: "island_life",
      icon: <PalmIcon variant="outlined" />,
      enabled: isEnabled("island_life"),
    },
  ];
  const LifestyleCategories = [
    {
      title: "Backpacker Social Scene",
      value: "backpacker_social_scene",
      icon: <BackpackIcon variant="outlined" />,
      enabled: isEnabled("backpacker_social_scene"),
    },
    {
      title: "Laidback & Slow Living",
      value: "laidback_and_slow_living",
      icon: (
        <SunbedIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("laidback_and_slow_living")
              ? theme.palette.common.grey[300]
              : isSelected("laidback_and_slow_living")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("laidback_and_slow_living"),
    },
    {
      title: "Adrenaline & Extreme Sports",
      value: "adrenaline_and_extreme_sports",
      icon: <ParachuteIcon variant="outlined" />,
      enabled: isEnabled("adrenaline_and_extreme_sports"),
    },
    {
      title: "Foodie & Wine Regions",
      value: "foodie_and_wine_regions",
      icon: <CheersIcon variant="outlined" />,
      enabled: isEnabled("foodie_and_wine_regions"),
    },
    {
      title: "Surf & Beach Culture",
      value: "surf_and_beach_culture",
      icon: (
        <KayakIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("surf_and_beach_culture")
              ? theme.palette.common.grey[300]
              : isSelected("surf_and_beach_culture")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("surf_and_beach_culture"),
    },
    {
      title: "Hiking & National Park",
      value: "hiking_and_national_parks",
      icon: (
        <ParkIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("hiking_and_national_parks")
              ? theme.palette.common.grey[300]
              : isSelected("hiking_and_national_parks")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
          fill={
            !isEnabled("hiking_and_national_parks")
              ? theme.palette.common.grey[300]
              : isSelected("hiking_and_national_parks")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("hiking_and_national_parks"),
    },
    {
      title: "Music & Art Hubs",
      value: "music_and_arts_hubs",
      icon: <GuitarIcon variant="outlined" />,
      enabled: isEnabled("music_and_arts_hubs"),
    },
    {
      title: "Wildlife & Conservation",
      value: "wildlife_and_conservation",
      icon: (
        <DeerIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("wildlife_and_conservation")
              ? theme.palette.common.grey[300]
              : isSelected("wildlife_and_conservation")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("wildlife_and_conservation"),
    },
    {
      title: "Diving & Marine Adventures",
      value: "diving_and_marine_adventures",
      icon: (
        <ScubaMaskIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("diving_and_marine_adventures")
              ? theme.palette.common.grey[300]
              : isSelected("diving_and_marine_adventures")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("diving_and_marine_adventures"),
    },
    {
      title: "Culture & Heritage",
      value: "cultural_and_indigenous_experiences",
      icon: <SignpostIcon variant="outlined" />,
      enabled: isEnabled("cultural_and_indigenous_experiences"),
    },
    {
      title: "Stargazing & Serenity",
      value: "stargazing_and_remote_tranquility",
      icon: (
        <TelescopeIcon
          variant="outlined"
          className={styles.strokeIcon}
          stroke={
            !isEnabled("stargazing_and_remote_tranquility")
              ? theme.palette.common.grey[300]
              : isSelected("stargazing_and_remote_tranquility")
              ? theme.palette.text.primary
              : theme.palette.common.grey[450]
          }
        />
      ),
      enabled: isEnabled("stargazing_and_remote_tranquility"),
    },
    {
      title: "Volcanic Wonders",
      value: "volcanic_and_geothermal_wonders",
      icon: <VolcanoIcon variant="outlined" />,
      enabled: isEnabled("volcanic_and_geothermal_wonders"),
    },
  ];

  const shuffleCategories = (categories: Array<any>) => {
    for (let i = categories.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [categories[i], categories[j]] = [categories[j], categories[i]];
    }
    return categories;
  };

  const toggle = (category: CategoryType) => {
    if (!category.enabled) return;
    if (selectedCategories?.includes(category.value)) {
      onChangeCategory(
        selectedCategories.filter((each) => each !== category.value)
      );
      track("Job Category Deselected", {
        eventId: "job-category-deselected",
        category: category.value,
      });
    } else {
      onChangeCategory(selectedCategories.concat(category.value));
      track("Job Category Selected", {
        eventId: "job-category-selected",
        category: category.value,
      });
    }
  };

  const sortedClimateCategories = [...ClimateCategories].sort(
    (first, second) => {
      if (second.enabled) return 1;
      if (first.enabled) return -1;
      return 0;
    }
  );

  const sortedLifestyleCategories = [...LifestyleCategories].sort(
    (first, second) => {
      if (second.enabled) return 1;
      if (first.enabled) return -1;
      return 0;
    }
  );

  const allCategories = [
    ...sortedLifestyleCategories,
    ...sortedClimateCategories,
  ];
  const allSortedCategories = [...allCategories].sort((first, second) => {
    if (isSelected(first.value) && first.enabled) return -1;
    if (isSelected(second.value)) return 1;
    return 0;
  });

  const allEnabledCategories = [...allCategories].sort((first, second) => {
    if (first.enabled) return -1;
    if (second.enabled) return 1;
    return 0;
  });

  const allSelectedCategories = [...allEnabledCategories].sort(
    (first, second) => {
      if (isSelected(first.value) && first.enabled) return -1;
      if (isSelected(second.value)) return 1;
      return 0;
    }
  );

  const selectedSortedClimateCategories = [...sortedClimateCategories].sort(
    (first, second) => {
      if (isSelected(first.value) && first.enabled) return -1;
      if (isSelected(second.value)) return 1;
      return 0;
    }
  );

  const selectedSortedLifestyleCategories = [...sortedLifestyleCategories].sort(
    (first, second) => {
      if (isSelected(first.value) && first.enabled) return -1;
      if (isSelected(second.value)) return 1;
      return 0;
    }
  );

  const countLabel = isLoading
    ? ""
    : totalCount < 10
    ? totalCount
    : totalCount < 50
    ? "+10"
    : totalCount < 100
    ? "+50"
    : "+100";

  const mobileActions = [
    {
      onClick: () => setIsPopupOpen(false),
      type: "primary",
      label: isLoading ? "Loading..." : `Show ${countLabel} results`,
      isLoading: isLoading,
    },
  ];

  return (
    <>
      {false ? (
        <div className={styles.mobileRoot} ref={mobileContainerRef}>
          <Typography
            variant="h6"
            className={styles.mobileTitle}
            color="text.secondary"
          >
            Filter jobs based on your interests.
          </Typography>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              {[1, 2, 3].map((each) => (
                <div key={each} className={styles.loadingItem}>
                  <Skeleton
                    variant="circular"
                    height={40}
                    width={40}
                    className={styles.loading}
                  />
                  <Skeleton variant="text" width={60} />
                </div>
              ))}
            </div>
          ) : (
            <Swiper
              className={cx(styles.swiper, styles.categories)}
              modules={[Navigation]}
              // navigation
              slidesPerView={mobileSlidesPerView}
              initialSlide={0}
              spaceBetween={12}
              onSwiper={setMobileSwiperObj}
            >
              {allSelectedCategories.slice(0, 6)?.map((category) => (
                <SwiperSlide
                  onClick={() => toggle(category)}
                  key={category.value}
                  className={cx(styles.category, {
                    [styles.selectedCategory]:
                      isSelected(category.value) && category.enabled,
                    [styles.disabledCategory]: !category.enabled,
                  })}
                >
                  {category.icon}

                  <Typography variant="body2">{category.title}</Typography>
                </SwiperSlide>
              ))}
              <SwiperSlide
                onClick={() => setIsPopupOpen(true)}
                className={cx(styles.category, {})}
              >
                <CategoriesIcon className={styles.categoriesIcon} />
                <Typography variant="body2" className={styles.categoriesText}>
                  View all categories
                </Typography>
              </SwiperSlide>
            </Swiper>
          )}
          {isPopupOpen && (
            <MobilePopup
              onClose={() => setIsPopupOpen(false)}
              actions={mobileActions}
            >
              <div className={styles.mobilePopupRoot}>
                <Typography variant="h6">Climate & Environment</Typography>
                <div className={styles.categoryContainer}>
                  {ClimateCategories.map((eachCategory) => (
                    <div
                      key={eachCategory.value}
                      role="presentation"
                      onClick={() => toggle(eachCategory)}
                      className={cx(styles.mobileCategoryItem, {
                        [styles.mobileSelectedCategory]:
                          isSelected(eachCategory.value) &&
                          eachCategory.enabled,
                        [styles.mobileDisabledCategory]: !eachCategory.enabled,
                      })}
                    >
                      {eachCategory.icon}
                      <Typography
                        variant="body2"
                        className={styles.mobileCategoryItemTitle}
                      >
                        {eachCategory.title}
                      </Typography>
                    </div>
                  ))}
                </div>
                <Typography variant="h6">Lifestyle & Interest</Typography>
                <div className={styles.categoryContainer}>
                  {LifestyleCategories.map((eachCategory) => (
                    <div
                      key={eachCategory.value}
                      role="presentation"
                      onClick={() => toggle(eachCategory)}
                      className={cx(styles.mobileCategoryItem, {
                        [styles.mobileSelectedCategory]:
                          isSelected(eachCategory.value) &&
                          eachCategory.enabled,
                        [styles.mobileDisabledCategory]: !eachCategory.enabled,
                        [styles.largeItem]: eachCategory.title.length > 27,
                      })}
                    >
                      {eachCategory.icon}
                      <Typography
                        variant="body2"
                        className={styles.mobileCategoryItemTitle}
                      >
                        {eachCategory.title}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </MobilePopup>
          )}
        </div>
      ) : (
        <div ref={containerRef} className={styles.root}>
          <Typography variant="subtitle2" className={styles.title}>
            Climate & Environment
          </Typography>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              {[1, 2, 3].map((each) => (
                <div key={each} className={styles.loadingItem}>
                  <Skeleton
                    variant="circular"
                    height={40}
                    width={40}
                    className={styles.loading}
                  />
                  <Skeleton variant="text" width={60} />
                </div>
              ))}
            </div>
          ) : (
            <Swiper
              className={cx(styles.swiper, styles.categories)}
              modules={[Navigation]}
              navigation={!isMobile}
              slidesPerView={firstSlidesPerView}
              initialSlide={0}
              spaceBetween={40}
              onSwiper={setFirstSwiperObj}
            >
              {selectedSortedClimateCategories?.map((category) => (
                <SwiperSlide
                  onClick={() => toggle(category)}
                  key={category.value}
                  className={cx(styles.category, {
                    [styles.selectedCategory]:
                      isSelected(category.value) && category.enabled,
                    [styles.disabledCategory]: !category.enabled,
                  })}
                >
                  {category.icon}

                  <Typography variant="body2">{category.title}</Typography>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          <Typography variant="subtitle2" className={styles.title}>
            Lifestyle & Interest
          </Typography>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              {[1, 2, 3].map((each) => (
                <div key={each} className={styles.loadingItem}>
                  <Skeleton
                    variant="circular"
                    height={40}
                    width={40}
                    className={styles.loading}
                  />
                  <Skeleton variant="text" width={60} />
                </div>
              ))}
            </div>
          ) : (
            <Swiper
              className={cx(styles.swiper, styles.categories)}
              modules={[Navigation]}
              navigation={!isMobile}
              slidesPerView={secondSlidesPerView}
              initialSlide={0}
              spaceBetween={40}
              onSwiper={setSecondSwiperObj}
            >
              {selectedSortedLifestyleCategories?.map((category) => (
                <SwiperSlide
                  onClick={() => toggle(category)}
                  key={category.value}
                  className={cx(styles.category, {
                    [styles.selectedCategory]:
                      isSelected(category.value) && category.enabled,
                    [styles.disabledCategory]: !category.enabled,
                  })}
                >
                  {category.icon}

                  <Typography variant="body2">{category.title}</Typography>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      )}
    </>
  );
};

export default CategoryFilter;
